import { A as JsonNonNullSchema, C as MethodSlugSchema, D as ASSET_TYPES, E as ASSET_EXTENSIONS, M as JsonScalarSchema, N as JsonSchema, O as zAsset, S as MethodSchema, T as Methods, _ as zMarkdownPreparation, a as titleSchemaWithMaxLength, b as zMarkdownText, d as MARKDOWN_ELEMENT_PATTERNS, f as MARKDOWN_PRESETS, g as zMarkdownDescription, h as zMarkdown, i as TitleSchema, j as JsonObjectSchema, k as JsonNonNullScalarSchema, m as visibleMarkdownLength, n as SOFT_HYPHEN, p as markdownSchemaWithAllowed, r as SOFT_HYPHEN_MARKER, t as SlugSchema, u as MARKDOWN_ELEMENTS, v as zMarkdownSubtitle, w as MethodSlugs, x as zMarkdownTextDecoration, y as zMarkdownSubtitleWithMaxLength } from "./slug.schemas--_8HzbpI.js";
import { t as uuid } from "./uuid-B9dV-orX.js";
import { A as BrinSchema, C as PublicationTagDraftSchema, D as RetractionTagSchema, E as ReleaseTagSchema, O as Brin4Schema, S as zDateTime, T as PublicationTagSchema, _ as RgbColorSchema, a as EmailSchema, b as TimestampsSchema, c as ScoreSchema, d as GradeSchema, f as GradesSchema, g as HslColorSchema, h as HexColorSchema, i as PositiveIntegerSchema, k as Brin6Schema, l as InternalRoleSchema, m as CssColorSchema, n as HttpUrlSchema, o as BooleanSchema, p as ColorSchema, r as NonNegativeIntegerSchema, s as AcademicYearSchema, t as UuidSchema, u as GRADES, w as PublicationTagRetractedSchema, x as UpdatedAtSchema, y as CreatedAtSchema } from "./uuid.schemas-2cE9Lh4I.js";
import * as z$1 from "zod";

//#region src/models/api-key.schemas.ts
/**
* A schema reading a Spark API token into its parts.
*
* A factory rather than a constant because the accepted prefix and lengths are
* per API — there is no one token shape to describe.
* @param options Shape of the tokens to accept.
* @returns Schema decoding a token string into its token, id and secret.
*/
function apiTokenSchema(options) {
	const { prefix, minIdLength = 8, length = 32 } = options;
	const start = `${prefix}_`;
	const minLength = start.length + minIdLength + length;
	const expected = `expected string (${prefix}_...) of at least ${minLength} characters`;
	return z$1.string(expected).trim().startsWith(start, expected).min(minLength, expected).transform((token) => ({
		token,
		id: token.slice(start.length, token.length - length),
		secret: token.slice(token.length - length)
	}));
}
/**
* A schema reading an internally-issued Spark API token into the single key it
* describes.
*
* The result is a list because that is what the servers take for their key set,
* and an internal token stands for exactly one key. The key's `id` is generated
* rather than read from the token — the token carries no id of its own.
* @param options Shape of the tokens to accept, plus the label to give the key.
* @returns Schema decoding a token string into a one-key list.
*/
function internalApiKeySchema(options) {
	const { label = "Unnamed key", ...tokenOptions } = options;
	return apiTokenSchema(tokenOptions).transform(({ id, secret }) => [{
		id: uuid(4),
		label,
		keyPrefix: tokenOptions.prefix,
		keyId: id,
		keySecret: secret
	}]);
}

//#endregion
//#region src/models/cms/cms.schemas.ts
const CmsPublishedSchema = TimestampsSchema.extend({
	publicationTag: z$1.null(),
	retractionTag: RetractionTagSchema.nullable()
}).meta({
	name: "spark-cms-published",
	title: "CMS Published",
	description: "Base schema for published CMS entities, including publication and retraction tags, and timestamps.",
	examples: [{
		publicationTag: null,
		retractionTag: null,
		createdAt: "2023-01-01T12:00:00Z",
		updatedAt: "2023-01-01T12:00:00Z"
	}, {
		publicationTag: null,
		retractionTag: "remove-me",
		createdAt: "2023-01-01T12:00:00Z",
		updatedAt: "2023-01-01T12:00:00Z"
	}]
});
const CmsDraftSchema = TimestampsSchema.extend({
	publicationTag: PublicationTagDraftSchema,
	retractionTag: z$1.null()
}).meta({
	name: "spark-cms-draft",
	title: "CMS Draft",
	description: "Base schema for draft CMS entities, including publication and retraction tags, and timestamps.",
	examples: [{
		publicationTag: null,
		retractionTag: null,
		createdAt: "2023-01-01T12:00:00Z",
		updatedAt: "2023-01-01T12:00:00Z"
	}]
});
const CmsRetractedSchema = TimestampsSchema.extend({
	publicationTag: PublicationTagRetractedSchema,
	retractionTag: z$1.null()
}).meta({
	name: "spark-cms-retracted",
	title: "CMS Retracted",
	description: "Base schema for retracted CMS entities, including publication and retraction tags, and timestamps.",
	examples: [{
		publicationTag: null,
		retractionTag: null,
		createdAt: "2023-01-01T12:00:00Z",
		updatedAt: "2023-01-01T12:00:00Z"
	}]
});
/**
* @param schema Item fields.
* @param docs Metadata docs to set on schema.
* @returns Schema including item fields and CMS fields.
*/
function createCmsSchema(schema, docs, options) {
	const draftExamples = generateDraftExamples(docs.examples);
	const retractedExamples = generateRetractedExamples(docs.examples);
	const publishedExamples = generatePublishedExamples(docs.examples);
	const publishedRaw = CmsPublishedSchema.extend(schema).meta({
		...docs,
		name: `${docs.name}-published`,
		title: `${docs.title} (Published)`,
		examples: publishedExamples
	});
	const draftRaw = CmsDraftSchema.extend(schema).meta({
		...docs,
		name: `${docs.name}-draft`,
		title: `${docs.title} (Draft)`,
		examples: draftExamples
	});
	const retractedRaw = CmsRetractedSchema.extend(schema).meta({
		...docs,
		name: `${docs.name}-retracted`,
		title: `${docs.title} (Retracted)`,
		examples: retractedExamples
	});
	const published = options?.preprocess ? z$1.preprocess(options.preprocess, publishedRaw) : publishedRaw;
	const draft = options?.preprocess ? z$1.preprocess(options.preprocess, draftRaw) : draftRaw;
	const retracted = options?.preprocess ? z$1.preprocess(options.preprocess, retractedRaw) : retractedRaw;
	return z$1.xor([
		published,
		draft,
		retracted
	]).meta({
		...docs,
		examples: [
			...publishedExamples,
			...draftExamples,
			...retractedExamples
		]
	});
}
function generatePublishedExamples(examples) {
	return examples.flatMap((example) => [{
		...example,
		publicationTag: null,
		retractionTag: null,
		createdAt: "2023-01-01T12:00:00Z",
		updatedAt: "2023-01-01T12:23:00Z"
	}, {
		...example,
		publicationTag: null,
		retractionTag: "winter-23",
		createdAt: "2023-01-01T12:00:00Z",
		updatedAt: "2023-01-01T12:23:00Z"
	}]);
}
function generateRetractedExamples(examples) {
	return examples.map((example) => ({
		...example,
		publicationTag: "winter-23_depublished",
		retractionTag: null,
		createdAt: "2023-01-01T12:00:00Z",
		updatedAt: "2023-01-01T12:23:00Z"
	}));
}
function generateDraftExamples(examples) {
	return examples.map((example) => ({
		...example,
		publicationTag: "winter-23",
		retractionTag: null,
		createdAt: "2023-01-01T12:00:00Z",
		updatedAt: "2023-01-01T12:23:00Z"
	}));
}

//#endregion
//#region src/parsing/codecs/json.ts
/**
* @param schema Schema to parse from and encode as json.
* @param options
* @returns zod schema to use.
*/
const jsonCodec = (schema, options) => z$1.codec(z$1.string(), schema, {
	decode: (jsonString, ctx) => {
		try {
			return JSON.parse(jsonString);
		} catch (err) {
			if (options?.allowPassthrough) return jsonString;
			ctx.issues.push({
				code: "invalid_format",
				format: "json",
				input: jsonString,
				message: err.message
			});
			return z$1.NEVER;
		}
	},
	encode: (value) => JSON.stringify(value)
});

//#endregion
//#region src/parsing/codecs/string-separated-array.ts
/**
* Codec between separator-joined strings and a typed array.
*
* Decodes both a single joined string (`"a,b"`) and an array of (possibly
* joined) strings (`["a", "b,c"]`, as produced by repeated query parameters)
* into an array validated against the element schema. Encodes back to a
* single joined string.
*
* @param schema Schema each separated value is validated against.
* @param options
* @returns zod schema to use.
*/
const stringSeparatedArray = (schema, options) => {
	const separator = options?.separator ?? ",";
	const trim = options?.trim ?? true;
	const removeEmpty = options?.removeEmpty ?? true;
	return z$1.codec(z$1.union([z$1.string(), z$1.array(z$1.string())]), z$1.array(schema), {
		decode: (input) => (input !== void 0 ? Array.isArray(input) ? input : [input] : []).flatMap((value) => value.split(separator)).map((value) => trim ? value.trim() : value).filter((value) => !removeEmpty || value !== ""),
		encode: (values) => values.join(separator)
	});
};

//#endregion
export { ASSET_EXTENSIONS, ASSET_TYPES, AcademicYearSchema, BooleanSchema, Brin4Schema, Brin6Schema, BrinSchema, ColorSchema, CreatedAtSchema, CssColorSchema, EmailSchema, GRADES, GradeSchema, GradesSchema, HexColorSchema, HslColorSchema, HttpUrlSchema, InternalRoleSchema, JsonNonNullScalarSchema, JsonNonNullSchema, JsonObjectSchema, JsonScalarSchema, JsonSchema, MARKDOWN_ELEMENTS, MARKDOWN_ELEMENT_PATTERNS, MARKDOWN_PRESETS, MethodSchema, MethodSlugSchema, MethodSlugs, Methods, NonNegativeIntegerSchema, PositiveIntegerSchema, PublicationTagDraftSchema, PublicationTagRetractedSchema, PublicationTagSchema, ReleaseTagSchema, RetractionTagSchema, RgbColorSchema, SOFT_HYPHEN, SOFT_HYPHEN_MARKER, ScoreSchema, SlugSchema, TimestampsSchema, TitleSchema, UpdatedAtSchema, UuidSchema, apiTokenSchema, createCmsSchema, internalApiKeySchema, jsonCodec, markdownSchemaWithAllowed, stringSeparatedArray, titleSchemaWithMaxLength, visibleMarkdownLength, zAsset, zDateTime, zMarkdown, zMarkdownDescription, zMarkdownPreparation, zMarkdownSubtitle, zMarkdownSubtitleWithMaxLength, zMarkdownText, zMarkdownTextDecoration };